import { useState } from 'react';
import { Link } from 'react-router-dom';

type LoginMethod = 'email' | 'phone';

export default function LoginForm({ onSwitch }: { onSwitch: () => void }) {
  const [method, setMethod] = useState<LoginMethod>('phone');
  const [showPassword, setShowPassword] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const sendCode = () => {
    setCodeSent(true);
    setCountdown(60);
    const timer = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) { clearInterval(timer); return 0; }
        return c - 1;
      });
    }, 1000);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const form = e.currentTarget;
    const data = new FormData(form);
    const body = new URLSearchParams();
    data.forEach((value, key) => body.append(key, value.toString()));
    try {
      await fetch('https://readdy.ai/api/form/d7d61tvsch5bvrijfc90', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-10">
        <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
          <i className="ri-check-double-line text-green-500 text-2xl"></i>
        </div>
        <h3 className="text-xl font-bold text-gray-800 mb-2">登录成功！</h3>
        <p className="text-gray-500 text-sm mb-6">欢迎回到富缘佳旺，您的缘分之旅继续。</p>
        <Link to="/" className="inline-block px-8 py-3 bg-red-600 text-white rounded-full text-sm font-semibold hover:bg-red-700 transition-all cursor-pointer whitespace-nowrap">
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <input type="hidden" name="form_type" value="会员登录" />
      <input type="hidden" name="login_method" value={method === 'phone' ? '手机号' : '邮箱'} />

      {/* Method Toggle */}
      <div className="flex bg-gray-100 rounded-lg p-1">
        {(['phone', 'email'] as LoginMethod[]).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMethod(m)}
            className={`flex-1 py-2 rounded-md text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${method === m ? 'bg-white text-red-600 shadow-sm' : 'text-gray-500'}`}
          >
            {m === 'phone' ? <><i className="ri-smartphone-line mr-1"></i>手机号登录</> : <><i className="ri-mail-line mr-1"></i>邮箱登录</>}
          </button>
        ))}
      </div>

      {method === 'phone' ? (
        <>
          <div>
            <label className="text-xs text-gray-500 font-medium mb-1.5 block">手机号码</label>
            <div className="flex gap-2">
              <select name="country_code" className="border border-gray-200 rounded-lg px-2 py-2.5 text-sm focus:outline-none focus:border-red-400 cursor-pointer w-24">
                <option value="+86">+86 🇨🇳</option>
                <option value="+1">+1 🇺🇸</option>
                <option value="+44">+44 🇬🇧</option>
                <option value="+61">+61 🇦🇺</option>
                <option value="+65">+65 🇸🇬</option>
              </select>
              <input
                name="phone"
                type="tel"
                placeholder="请输入手机号"
                required
                className="flex-1 border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-red-400"
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500 font-medium mb-1.5 block">验证码</label>
            <div className="flex gap-2">
              <input
                name="sms_code"
                type="text"
                placeholder="请输入验证码"
                maxLength={6}
                className="flex-1 border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-red-400"
              />
              <button
                type="button"
                onClick={sendCode}
                disabled={countdown > 0}
                className={`px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${countdown > 0 ? 'bg-gray-100 text-gray-400' : 'bg-red-50 text-red-600 hover:bg-red-100'}`}
              >
                {countdown > 0 ? `${countdown}s` : codeSent ? '重新发送' : '发送验证码'}
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          <div>
            <label className="text-xs text-gray-500 font-medium mb-1.5 block">邮箱地址</label>
            <input
              name="email"
              type="email"
              placeholder="请输入邮箱地址"
              required
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-red-400"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 font-medium mb-1.5 block">密码</label>
            <div className="relative">
              <input
                name="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="请输入密码"
                required
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-red-400 pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer"
              >
                <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
              </button>
            </div>
          </div>
        </>
      )}

      <div className="flex items-center justify-between text-xs">
        <label className="flex items-center gap-1.5 text-gray-500 cursor-pointer">
          <input type="checkbox" name="remember" className="rounded" /> 记住我
        </label>
        <a href="#" className="text-red-500 hover:underline cursor-pointer">忘记密码？</a>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full bg-gradient-to-r from-red-600 to-red-700 text-white py-3 rounded-full font-semibold text-sm hover:from-red-700 hover:to-red-800 transition-all cursor-pointer whitespace-nowrap disabled:opacity-60"
      >
        {loading ? <><i className="ri-loader-4-line animate-spin mr-1"></i>登录中...</> : '立即登录'}
      </button>

      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-gray-100"></div>
        <span className="text-xs text-gray-400">或使用第三方登录</span>
        <div className="flex-1 h-px bg-gray-100"></div>
      </div>

      <div className="flex gap-3">
        <button type="button" className="flex-1 flex items-center justify-center gap-2 border border-gray-200 rounded-lg py-2.5 text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">
          <i className="ri-wechat-line text-green-500 text-base"></i> 微信
        </button>
        <button type="button" className="flex-1 flex items-center justify-center gap-2 border border-gray-200 rounded-lg py-2.5 text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">
          <i className="ri-weibo-line text-red-400 text-base"></i> 微博
        </button>
        <button type="button" className="flex-1 flex items-center justify-center gap-2 border border-gray-200 rounded-lg py-2.5 text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">
          <i className="ri-apple-line text-gray-700 text-base"></i> Apple
        </button>
      </div>

      <p className="text-center text-xs text-gray-400">
        还没有账号？
        <button type="button" onClick={onSwitch} className="text-red-500 font-medium ml-1 cursor-pointer hover:underline">免费注册</button>
      </p>
    </form>
  );
}
